/**
 * CodeMirrorEditor — React wrapper for CM6 replacing the old textarea editor.
 *
 * Features:
 * - Markdown syntax highlighting with language-specific fenced code blocks
 * - Blame gutter + diff bar as CM6 extensions (automatic line-height sync)
 * - Resizable blame gutter (drag handle + double-click reset, width persisted)
 * - Dual font mode (proportional / mono) via CM6 Compartments
 * - External content updates without losing cursor position when possible
 * - Zero-lag input via CM6's direct DOM rendering
 * - Native spellcheck, autocorrect, IME, and clipboard on mobile
 */
import { useRef, useEffect, useCallback, useState, type RefObject } from 'react'
import { EditorView, keymap, drawSelection, highlightActiveLine, lineNumbers, highlightActiveLineGutter } from '@codemirror/view'
import { EditorState, Compartment } from '@codemirror/state'
import { markdown, markdownLanguage } from '@codemirror/lang-markdown'
import { languages } from '@codemirror/language-data'
import { defaultKeymap, history, historyKeymap } from '@codemirror/commands'
import { closeBrackets } from '@codemirror/autocomplete'

import {
    sbBaseExtensions, sbThemeProportional, sbThemeMono,
    blameGutter, setBlameLinesEffect,
    diffBarGutter, setDiffLinesEffect,
} from '../cm-extensions'
import type { BlameLine } from '../engine/types'

// ── Constants ──────────────────────────────────────────────────

const BLAME_MIN = 60
const BLAME_MAX = 360
const BLAME_DEFAULT = 140

// ── Types ──────────────────────────────────────────────────────

export interface CodeMirrorEditorProps {
    content: string
    fontMode: 'proportional' | 'mono'
    showLineNumbers: boolean
    isMobile: boolean
    blameLines: BlameLine[]
    blameEnabled: boolean
    blameStale: boolean
    blameWidth: number
    onBlameWidthChange?: (width: number) => void
    /** Called on mouseup — persist final width to localStorage */
    onBlameWidthCommit?: (width: number) => void
    readOnly: boolean
    onChange: (value: string) => void
    onSelectionChange?: (text: string, rect: DOMRect | null) => void
    /** Ref exposed so parent can dispatch CM6 commands (e.g., insert markdown) */
    editorViewRef?: RefObject<EditorView | null>
    /** Cursor offset to restore on mount (0 = start of doc). */
    initialSelection?: number
}

// ── Component ──────────────────────────────────────────────────

export default function CodeMirrorEditor({
    content,
    fontMode,
    showLineNumbers: showLineNums,
    isMobile,
    blameLines,
    blameEnabled,
    blameStale,
    blameWidth,
    onBlameWidthChange,
    onBlameWidthCommit,
    readOnly,
    onChange,
    onSelectionChange,
    editorViewRef,
    initialSelection,
}: CodeMirrorEditorProps) {
    const containerRef = useRef<HTMLDivElement>(null)
    const viewRef = useRef<EditorView | null>(null)
    const fontCompartment = useRef(new Compartment())
    const readOnlyCompartment = useRef(new Compartment())
    const blameCompartment = useRef(new Compartment())
    const lineNumCompartment = useRef(new Compartment())

    // Track the last content value dispatched from CM6's updateListener
    // to prevent feedback loops without the race conditions of a boolean flag.
    const lastCM6Content = useRef<string | null>(null)

    // Keep onChange in a ref so we don't recreate the editor when it changes
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    const onSelectionChangeRef = useRef(onSelectionChange)
    onSelectionChangeRef.current = onSelectionChange

    // ── Create editor ──────────────────────────────────────────

    useEffect(() => {
        if (!containerRef.current) return

        const fontTheme = fontMode === 'mono' ? sbThemeMono : sbThemeProportional

        // Clamp initial cursor to valid range
        const cursorPos = Math.min(initialSelection ?? 0, content.length)

        const state = EditorState.create({
            doc: content,
            selection: { anchor: cursorPos },
            extensions: [
                // Core editing
                history(),
                keymap.of([...defaultKeymap, ...historyKeymap]),
                closeBrackets(),
                drawSelection(),
                highlightActiveLine(),
                EditorView.lineWrapping,

                // Markdown language support with nested language highlighting
                markdown({
                    base: markdownLanguage,
                    codeLanguages: languages,
                }),

                // Theme + syntax highlighting
                ...sbBaseExtensions,
                fontCompartment.current.of(fontTheme),

                // Editable state
                readOnlyCompartment.current.of(EditorState.readOnly.of(readOnly)),

                // Blame gutter — leftmost, only when enabled (Compartment allows dynamic add/remove)
                blameCompartment.current.of(blameEnabled ? blameGutter({ compact: isMobile }) : []),

                // Line numbers — after blame, close to content (dynamic via Compartment)
                lineNumCompartment.current.of(
                    showLineNums ? [lineNumbers(), highlightActiveLineGutter()] : []
                ),

                // Diff bar — rightmost gutter, always visible
                diffBarGutter(),

                // Change listener
                EditorView.updateListener.of(update => {
                    if (update.docChanged) {
                        const newDoc = update.state.doc.toString()
                        lastCM6Content.current = newDoc
                        onChangeRef.current(newDoc)
                    }
                    // Selection change → floating toolbar
                    if (update.selectionSet && onSelectionChangeRef.current) {
                        const sel = update.state.selection.main
                        if (!sel.empty) {
                            const text = update.state.sliceDoc(sel.from, sel.to)
                            const coords = update.view.coordsAtPos(sel.from)
                            if (coords) {
                                const rect = new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top)
                                onSelectionChangeRef.current(text, rect)
                            }
                        } else {
                            onSelectionChangeRef.current('', null)
                        }
                    }
                }),
            ],
        })

        const view = new EditorView({
            state,
            parent: containerRef.current,
        })

        viewRef.current = view
        if (editorViewRef) (editorViewRef as React.MutableRefObject<EditorView | null>).current = view

        // Focus editor and scroll cursor into view after mount
        // Skip auto-focus on mobile — virtual keyboard would obscure content
        const isTouch = 'ontouchstart' in window
        requestAnimationFrame(() => {
            if (!isTouch) view.focus()
            if (cursorPos > 0) {
                view.dispatch({ effects: EditorView.scrollIntoView(cursorPos, { y: 'center' }) })
            }
        })

        // Push initial blame + diff data
        view.dispatch({
            effects: [
                setBlameLinesEffect.of({
                    lines: blameLines,
                    enabled: blameEnabled,
                    stale: blameStale,
                }),
                setDiffLinesEffect.of(blameLines),
            ],
        })

        return () => {
            view.destroy()
            viewRef.current = null
            if (editorViewRef) (editorViewRef as React.MutableRefObject<EditorView | null>).current = null
        }
        // Only recreate on mount/unmount — all other updates handled via effects below
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // ── Sync external content changes ──────────────────────────

    useEffect(() => {
        const view = viewRef.current
        if (!view) return

        // If this content value matches what CM6 last dispatched, it's our own
        // keystroke echoing back through React state — skip to avoid feedback loop.
        if (content === lastCM6Content.current) {
            return
        }

        // External content update (e.g., sync merge result, pull from remote).
        // Use minimal diff (common prefix/suffix) so CM6 preserves cursor position.
        const currentDoc = view.state.doc.toString()
        if (content !== currentDoc) {
            const newContent = content ?? ''
            // Find common prefix length
            let prefixLen = 0
            const minLen = Math.min(currentDoc.length, newContent.length)
            while (prefixLen < minLen && currentDoc[prefixLen] === newContent[prefixLen]) {
                prefixLen++
            }
            // Find common suffix length (don't overlap with prefix)
            let suffixLen = 0
            const maxSuffix = minLen - prefixLen
            while (
                suffixLen < maxSuffix &&
                currentDoc[currentDoc.length - 1 - suffixLen] === newContent[newContent.length - 1 - suffixLen]
            ) {
                suffixLen++
            }

            const from = prefixLen
            const to = currentDoc.length - suffixLen
            const insert = newContent.slice(prefixLen, newContent.length - suffixLen)

            if (from !== to || insert.length > 0) {
                view.dispatch({ changes: { from, to, insert } })
            }
        }
    }, [content])

    // ── Sync font mode ─────────────────────────────────────────

    useEffect(() => {
        const view = viewRef.current
        if (!view) return
        const fontTheme = fontMode === 'mono' ? sbThemeMono : sbThemeProportional
        view.dispatch({
            effects: fontCompartment.current.reconfigure(fontTheme),
        })
    }, [fontMode])

    // ── Sync readOnly ──────────────────────────────────────────

    useEffect(() => {
        const view = viewRef.current
        if (!view) return
        view.dispatch({
            effects: readOnlyCompartment.current.reconfigure(EditorState.readOnly.of(readOnly)),
        })
    }, [readOnly])

    // ── Sync line numbers ──────────────────────────────────────

    useEffect(() => {
        const view = viewRef.current
        if (!view) return
        view.dispatch({
            effects: lineNumCompartment.current.reconfigure(
                showLineNums ? [lineNumbers(), highlightActiveLineGutter()] : []
            ),
        })
    }, [showLineNums])

    // ── Sync blame data ────────────────────────────────────────

    useEffect(() => {
        const view = viewRef.current
        if (!view) return

        // Reconfigure blame gutter: add/remove entire extension based on enabled state
        view.dispatch({
            effects: [
                blameCompartment.current.reconfigure(
                    blameEnabled ? blameGutter({ compact: isMobile }) : []
                ),
                setBlameLinesEffect.of({
                    lines: blameLines,
                    enabled: blameEnabled,
                    stale: blameStale,
                }),
                setDiffLinesEffect.of(blameLines),
            ],
        })
    }, [blameLines, blameEnabled, blameStale, isMobile])

    // ── Blame gutter resize (mirrors sidebar resize pattern in App.tsx) ──

    const blameResizing = useRef(false)
    const onBlameWidthChangeRef = useRef(onBlameWidthChange)
    onBlameWidthChangeRef.current = onBlameWidthChange
    const onBlameWidthCommitRef = useRef(onBlameWidthCommit)
    onBlameWidthCommitRef.current = onBlameWidthCommit

    const [isBlameResizing, setIsBlameResizing] = useState(false)

    const handleBlameResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault()
        blameResizing.current = true
        setIsBlameResizing(true)
        document.body.style.cursor = 'col-resize'
        document.body.style.userSelect = 'none'

        // Capture gutter left edge offset — blame gutter doesn't start at viewport x=0
        const gutterEl = containerRef.current?.querySelector('.sb-blame-gutter')
        const gutterLeft = gutterEl?.getBoundingClientRect().left ?? 0
        let lastWidth = 0

        const onMove = (ev: MouseEvent) => {
            if (!blameResizing.current) return
            const w = Math.min(BLAME_MAX, Math.max(BLAME_MIN, ev.clientX - gutterLeft))
            lastWidth = w
            onBlameWidthChangeRef.current?.(w)
        }
        const onUp = () => {
            blameResizing.current = false
            setIsBlameResizing(false)
            document.body.style.cursor = ''
            document.body.style.userSelect = ''
            // Persist final width (not on every mousemove — avoids sync I/O jank)
            if (lastWidth > 0) onBlameWidthCommitRef.current?.(lastWidth)
            window.removeEventListener('mousemove', onMove)
            window.removeEventListener('mouseup', onUp)
        }
        window.addEventListener('mousemove', onMove)
        window.addEventListener('mouseup', onUp)
    }, [])

    const handleBlameResizeDoubleClick = useCallback(() => {
        onBlameWidthChangeRef.current?.(BLAME_DEFAULT)
        onBlameWidthCommitRef.current?.(BLAME_DEFAULT)
    }, [])

    // ── Render ──────────────────────────────────────────────────

    return (
        <div
            className={`cm-editor-wrapper${isBlameResizing ? ' blame-resizing' : ''}`}
            style={{ '--blame-gutter-width': `${blameWidth}px` } as React.CSSProperties}
        >
            <div
                ref={containerRef}
                className="cm-editor-container"
                data-testid="codemirror-editor"
            />
            {blameEnabled && (
                <div
                    className="blame-resize-handle"
                    role="separator"
                    aria-orientation="vertical"
                    aria-valuenow={blameWidth}
                    aria-valuemin={BLAME_MIN}
                    aria-valuemax={BLAME_MAX}
                    aria-label="Resize blame gutter"
                    tabIndex={0}
                    style={{ left: blameWidth }}
                    onMouseDown={handleBlameResizeStart}
                    onDoubleClick={handleBlameResizeDoubleClick}
                    title="Drag to resize blame, double-click to reset"
                />
            )}
        </div>
    )
}
